import React from "react";
import { Mail, Phone, MapPin, Instagram, Linkedin, ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { BRAND, NAV } from "@/lib/brand";

export default function Footer({ onOpenInquiry }) {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const navHref = (id) => (isHome ? `#${id}` : `/#${id}`);
  return (
    <footer
      id="contact"
      data-testid="site-footer"
      className="relative bg-zinc-950 text-white border-t border-zinc-200 scroll-mt-24"
    >
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 justify-between">
          <div className="lg:max-w-xl">
            <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-400 mb-4">
              Let&apos;s Talk
            </div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]">
              Buy your first
              <br />
              <span className="text-zinc-400">vending machine.</span>
            </h2>
            <p className="mt-6 text-zinc-400 max-w-md">
              Tell us your site, footfall and product mix — we&apos;ll come back with a
              shortlist and a quote within 24 hours.
            </p>
            <button
              data-testid="footer-cta-quote"
              onClick={onOpenInquiry}
              className="mt-8 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-full px-7 py-4 transition shadow-[0_10px_30px_rgba(16,185,129,0.35)]"
            >
              Get a Free Quote
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Explore
              </div>
              <ul className="space-y-3 text-sm">
                {NAV.map((n) => (
                  <li key={n.id}>
                    <a
                      href={navHref(n.id)}
                      className="text-zinc-300 hover:text-white transition"
                      data-testid={`footer-nav-${n.id}`}
                    >
                      {n.label}
                    </a>
                  </li>
                ))}
                <li>
                  <Link
                    to="/faq"
                    data-testid="footer-nav-faq"
                    className="text-zinc-300 hover:text-white transition"
                  >
                    FAQ
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Machines
              </div>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link
                    to="/products/refuel-core"
                    data-testid="footer-machine-core"
                    className="text-zinc-300 hover:text-white transition"
                  >
                    Refuel CORE — Snack & Beverage
                  </Link>
                </li>
                <li>
                  <Link
                    to="/products/refuel-lite"
                    data-testid="footer-machine-lite"
                    className="text-zinc-300 hover:text-white transition"
                  >
                    Refuel LITE — Compact Cashless
                  </Link>
                </li>
                <li>
                  <Link
                    to="/products/refuel-max"
                    data-testid="footer-machine-max"
                    className="text-zinc-300 hover:text-white transition"
                  >
                    Refuel MAX — High-volume
                  </Link>
                </li>
                <li>
                  <Link
                    to="/products/refuel-safe"
                    data-testid="footer-machine-safe"
                    className="text-zinc-300 hover:text-white transition"
                  >
                    Refuel SAFE — PPE
                  </Link>
                </li>
                <li>
                  <Link
                    to="/products"
                    data-testid="footer-machines-index"
                    className="text-zinc-300 hover:text-white transition"
                  >
                    All machines →
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Cities
              </div>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link to="/vending-machines/vadodara" data-testid="footer-city-vadodara" className="text-zinc-300 hover:text-white transition">Vadodara</Link>
                </li>
                <li>
                  <Link to="/vending-machines/ahmedabad" data-testid="footer-city-ahmedabad" className="text-zinc-300 hover:text-white transition">Ahmedabad</Link>
                </li>
                <li>
                  <Link to="/vending-machines/mumbai" data-testid="footer-city-mumbai" className="text-zinc-300 hover:text-white transition">Mumbai</Link>
                </li>
                <li>
                  <Link to="/vending-machines/pune" data-testid="footer-city-pune" className="text-zinc-300 hover:text-white transition">Pune</Link>
                </li>
                <li>
                  <Link to="/vending-machines/bangalore" data-testid="footer-city-bangalore" className="text-zinc-300 hover:text-white transition">Bangalore</Link>
                </li>
                <li>
                  <Link to="/vending-machines/delhi" data-testid="footer-city-delhi" className="text-zinc-300 hover:text-white transition">Delhi NCR</Link>
                </li>
                <li>
                  <Link to="/vending-machines/hyderabad" data-testid="footer-city-hyderabad" className="text-zinc-300 hover:text-white transition">Hyderabad</Link>
                </li>
                <li>
                  <Link to="/vending-machines" data-testid="footer-cities-index" className="text-zinc-300 hover:text-white transition">All cities →</Link>
                </li>
              </ul>
            </div>
            <div className="col-span-2 sm:col-span-2 lg:col-span-1">
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Contact
              </div>
              <ul className="space-y-3 text-sm text-zinc-300">
                <li className="flex items-start gap-3 min-w-0">
                  <Mail className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                  <a
                    href={`mailto:${BRAND.email}`}
                    data-testid="footer-email"
                    className="hover:text-white transition whitespace-nowrap"
                  >
                    {BRAND.email}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                  <a
                    href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                    data-testid="footer-phone"
                    className="hover:text-white transition"
                  >
                    {BRAND.phone}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                  <span>{BRAND.address}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-display text-2xl md:text-3xl font-extrabold tracking-tight leading-none">
              THECO<span className="text-emerald-400">V</span>ENTU<span className="text-amber-400">R</span>ES
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mt-1.5">
              {BRAND.tagline}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://www.linkedin.com/company/thecoventures"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Thecoventures on LinkedIn"
              data-testid="social-linkedin"
              className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 hover:border-emerald-400/50 transition"
            >
              <Linkedin className="w-4 h-4" />
            </a>
            <a
              href="https://www.instagram.com/thecoventures"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Thecoventures on Instagram"
              data-testid="social-instagram"
              className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 hover:border-emerald-400/50 transition"
            >
              <Instagram className="w-4 h-4" />
            </a>
          </div>
          <div className="text-xs text-zinc-500">
            © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}
